/**
 * Persiste sesión Supabase en localStorage sin llamar setSession().
 * Evita AbortError por locks internos de GoTrue cuando hay auth concurrente.
 */

import type { User } from '@supabase/supabase-js';
import { SUPABASE_STORAGE_KEY } from './supabaseClient';

interface PersistSessionInput {
  access_token: string;
  refresh_token: string;
  user?: User | null;
  expires_in?: number;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const b64 = token.split('.')[1];
    if (!b64) return null;
    const json = atob(b64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function buildUserFromAccessToken(accessToken: string): User | null {
  const payload = decodeJwtPayload(accessToken);
  if (!payload?.sub || typeof payload.sub !== 'string') return null;

  return {
    id: payload.sub,
    email: typeof payload.email === 'string' ? payload.email : '',
    aud: 'authenticated',
    role: 'authenticated',
    app_metadata: (payload.app_metadata as Record<string, unknown>) || {},
    user_metadata: (payload.user_metadata as Record<string, unknown>) || {},
    created_at: new Date().toISOString(),
  } as User;
}

export function persistSupabaseSession(session: PersistSessionInput): void {
  const user = session.user ?? buildUserFromAccessToken(session.access_token);
  if (!user) {
    throw new Error('No se pudo obtener usuario de los tokens');
  }

  const expiresIn = session.expires_in ?? 3600;
  const expiresAt = Math.floor(Date.now() / 1000) + expiresIn;

  const stored = {
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    expires_at: expiresAt,
    expires_in: expiresIn,
    token_type: 'bearer',
    user,
  };

  localStorage.setItem(SUPABASE_STORAGE_KEY, JSON.stringify(stored));
}

export function isAuthCallbackPath(): boolean {
  return typeof window !== 'undefined' && window.location.pathname === '/auth/callback';
}
